import { FC, useEffect, useState } from "react";
import HighFiveIcon from "@/icons/highFiveIcon";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import { List, ListItem, ListItemText, Typography } from "@mui/material";
import styles from "@/components/appointmentRequest/timeSlotPickConfirmation.module.css";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import type { RemainingManagerBookings } from "@/components/appointmentRequest/useRemainingManagerBookings";

interface Props {
  appointmentInformation?: any;
  managerBooking?: boolean;
  onAssignAnother?: () => void;
  repeatAssignmentState?: RemainingManagerBookings;
  onRetryAssignmentCheck?: () => void;
}

const TimeSlotPickConfirmation: FC<Props> = ({
  appointmentInformation,
  managerBooking = false,
  onAssignAnother,
  repeatAssignmentState = "checking",
  onRetryAssignmentCheck,
}) => {
  const { t } = useTranslation();
  usePageHeading(
    managerBooking
      ? t("manager_appointment_booking_successful")
      : t("appointment_booking_successful"),
  );
  const [currentAppointmentInformation, setCurrentAppointmentInformation] =
    useState<any>();
  const navigate = useNavigate();

  useEffect(() => {
    setCurrentAppointmentInformation(appointmentInformation);
  }, [appointmentInformation]);

  const navigateAfterConfirmation = () => {
    navigate(managerBooking ? "/" : "/todo/");
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const renderSecondaryText = () => {
    return (
      <div className={styles.secondaryText}>
        <span>{t("secondary_mail_text_slot_confirmation")}</span>
        <p>
          <span>
            {t("contact_our_service_team") + " "}

            <a
              className={styles.emailLink}
              href="mailto:service@kaerhealth.com"
            >
              {t("email_service_kaer")}
            </a>
          </span>
        </p>
      </div>
    );
  };

  const renderContent = () => {
    const dateTime = dayjs(currentAppointmentInformation?.datetime).format(
      "DD.MM.YYYY, HH:mm",
    );
    const description = (
      <List className={styles.noPadding}>
        <ListItem className={styles.noPadding}>
          <ListItemText
            primary={
              <Typography
                className={styles.typoUnderlined}
                component="span"
                variant="body2"
                color="text.primary"
              >
                {t(
                  managerBooking
                    ? "confirmation_description_process"
                    : "confirmation_expecting_you",
                )}
              </Typography>
            }
          />
        </ListItem>
        {currentAppointmentInformation?.examination_set.map(
          (examination: any) => {
            return (
              <ListItem
                key={examination.name + examination.id}
                className={styles.noPadding}
              >
                <ListItemText
                  secondaryTypographyProps={{ component: "div" }}
                  primary={
                    <Typography
                      className={styles.displayBlock}
                      component="span"
                      variant="body2"
                      color="text.primary"
                    >
                      {examination?.name}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{ whiteSpace: "preserve" }}
                    >
                      {examination?.description}
                    </Typography>
                  }
                />
              </ListItem>
            );
          },
        )}
      </List>
    );

    return (
      <>
        <div className={styles.content}>
          <div className={styles.iconContainer}>
            <HighFiveIcon width="160" height="159" />
          </div>
          <div className={styles.tableContainer}>
            <table className={styles.table} border={0}>
              <colgroup>
                <col className={styles.tableCol1} />
                <col className={styles.tableCol2} />
              </colgroup>
              <tbody>
                {managerBooking && (
                  <tr className={styles.tableRow}>
                    <td className={styles.tableHeader}>{t("worker")}</td>
                    <td className={styles.tableData}>
                      {currentAppointmentInformation?.subject?.name}
                    </td>
                  </tr>
                )}
                <tr className={styles.tableRow}>
                  <td className={styles.tableHeader}>
                    {t("confirmation_date_time")}
                  </td>
                  <td className={styles.tableData}>
                    <div>{dateTime} Uhr</div>
                  </td>
                </tr>
                <tr className={styles.tableRow}>
                  <td className={styles.tableDescription}>
                    {t("confirmation_description_process")}
                  </td>
                  <td className={styles.tableData}>
                    <div>{description}</div>
                  </td>
                </tr>
              </tbody>
            </table>
            {managerBooking && onAssignAnother && (
              <GenericButton
                onClick={
                  repeatAssignmentState === "error"
                    ? onRetryAssignmentCheck
                    : onAssignAnother
                }
                disabled={
                  repeatAssignmentState !== "available" &&
                  repeatAssignmentState !== "error"
                }
                className={styles.button}
              >
                {t(
                  {
                    checking: "loading",
                    available: "assign_another_worker_appointment",
                    noWorkers: "no_more_worker_assignments",
                    noSlots: "no_more_appointment_slots",
                    error: "retry",
                  }[repeatAssignmentState],
                )}
              </GenericButton>
            )}
            {managerBooking && repeatAssignmentState === "error" && (
              <Typography role="alert">
                {t("worker_booking_availability_check_failed")}
              </Typography>
            )}
            <GenericButton
              onClick={navigateAfterConfirmation}
              className={styles.button}
              variant={managerBooking ? "outlined" : "contained"}
            >
              {managerBooking ? (
                t("back_to_home")
              ) : !isMobile ? (
                t("new_todo_after_picked_slot")
              ) : (
                <>
                  {t("new_todo_after_picked_slot_short")}
                  <br />
                  {t("click_here")}
                </>
              )}
            </GenericButton>

            {!managerBooking && renderSecondaryText()}
          </div>
        </div>
      </>
    );
  };

  if (!currentAppointmentInformation) {
    return null;
  }
  return (
    <>
      <div className={styles.container}>{renderContent()}</div>
    </>
  );
};

export default TimeSlotPickConfirmation;
